// The detail viewer — the full-screen interactive stage you land on after
// clicking a gallery tile. It shows one model large on a pedestal (or, when the
// grid toggle is on, a fading tile floor) (drag or
// one-finger swipe to orbit with a little inertia, scroll or two-finger pinch to
// zoom, auto-spin), steps its variants and plays its animations. It owns its
// own WebGL context and render loop, started on `open` and parked on `close` so
// the gallery's thumbnail studio is the only thing drawing while you browse.

import * as THREE from "three";
import { instantiateModel, ModelInstance, instantiateInteraction, InteractionInstance, bakeSockets, buildModel, slotsOf, applySlots, type ColorRepresentation, type Interaction, type Model, type SocketFill } from "../lib";
import { addStudioLights } from "./lights";
import { versionLabel } from "./groups";
import { batchOf } from "../batches";
import { tagLabel, tagMeta } from "./tags";

/** The slice of a catalogue item the detail viewer needs (a `LoadedModel`). */
export interface DetailItem {
  model: Model;
  path: string;
  source: string;
  category: string;
}

/** One object opened in the detail viewer: its name plus its versions. A
 *  single-version object is just a group of one (no switcher is shown). */
export interface DetailGroup {
  /** Display name for the object ("Avenue"). */
  name: string;
  /** The object's versions, ordered base-first; the viewer switches between them. */
  members: DetailItem[];
}

export interface DetailOptions {
  /** Clicking a tag jumps back to the gallery filtered to it. */
  onTag(tag: string): void;
  /** Clicking the batch jumps to that production run's page. */
  onBatch(batchId: string): void;
  /** The back button / Escape asks the host to return to the gallery. */
  onClose(): void;
  /** Draw a small preview of `model`/`variantId` into `canvas` (the version
   *  switcher / variant grid). `tight` fills the tile more (no fixed padding). */
  renderThumb(canvas: HTMLCanvasElement, model: Model, variantId: string, tight?: boolean, slots?: Record<string, ColorRepresentation>): void;
  /** Report the model/variant now on the stage so the host can keep the URL in
   *  sync (a deep link restores the same variant after a reload). `variantId` is
   *  null when the default variant is showing. */
  onState(modelId: string, variantId: string | null): void;
  /** The user *explicitly* chose a variant (a chip click or the arrow keys),
   *  reporting its id so the host can carry that choice forward as the preferred
   *  default for the next object opened. */
  onVariantPick?(variantId: string): void;
  /** Look up another model by id — used to fetch the actor an interaction poses
   *  (the default worker). Returns undefined if the catalogue lacks it. */
  resolveModel(id: string): Model | undefined;
  /** The models that fit a socket, excluding the host `hostId`. When the socket
   *  has a `slotType`, matches models whose `fits` includes it; otherwise falls
   *  back to the legacy `accepts` tag filter. */
  mountablesFor(socket: { slotType?: string; accepts?: string[] }, hostId: string): Model[];
  /** Open a slot type's own page (its contract, the content that fits it and the
   *  hosts that accept it) — the hub both a host's sockets and a slotable model's
   *  `fits` link through to. */
  onSlotType(slotTypeId: string): void;
  /** The display label of a slot type, or undefined when the id names no known
   *  slot type (in which case it is not linkable). */
  slotTypeLabel(slotTypeId: string): string | undefined;
}

// The actor an interaction is previewed with when the model does not name one.
const DEFAULT_ACTOR = "worker-simple";

// The Age of MCP owner-colour palette (PLAYER_COLORS in the game) offered as
// preset swatches for a team-colour slot. A free colour input sits alongside, so
// any colour is reachable — the presets are just the authentic team set.
const TEAM_PRESETS = [
  "#e74c3c", "#3498db", "#f1c40f", "#9b59b6",
  "#e67e22", "#1abc9c", "#e91e63", "#00bcd4",
];

// The model rests on a small pedestal by default. A toolbar toggle swaps that
// pedestal for a fading floor grid: square tiles 1 world unit (metre) on a
// side, the lines fading out radially towards the horizon so the floor reads as
// endless rather than a slab with edges.
const PED_H = 0.1;           // pedestal height (also the model's lift in pedestal mode)
const TILE = 1;              // tile pitch in world units
const GRID_EXTENT = 21;      // half-size of the laid-out grid (14 tiles each way)
// The grid fades out in a ring sized to the model at stage time (see applyFloor):
// solid out to the model's footprint radius, gone GRID_FADE_MARGIN beyond it.
const GRID_FADE_MARGIN = 2;  // metres past the model's edge the lines fully vanish
const PITCH = THREE.MathUtils.degToRad(28); // default camera elevation
const AZIM = THREE.MathUtils.degToRad(35);  // default camera azimuth
const MIN_PITCH = 0.08; // keep the camera above the ground plane
const MAX_PITCH = 1.45;  // near top-down, short of flipping over the pole
const ROT_SPEED = 0.01;  // radians of orbit per pixel dragged (mouse drag or one-finger swipe)
const ZOOM_MIN = 0.15;
const ZOOM_MAX = 30;
const SPIN_RATE = 0.6;   // auto-spin speed, radians/s
// Touch inertia: a flick leaves the model gliding, the glide shedding all but
// this fraction of its speed each second so it coasts to rest in about a beat.
const GLIDE_DECAY = 0.0023;
const GLIDE_STOP = 0.02; // radians/s below which the glide is snapped to a halt

// The single colour of the endless world: the background and the floor are one
// and the same flat tone, so the ground reads as boundless. The floor carries no
// colour of its own (it is a transparent shadow-catcher over this), so however
// far you zoom out there is no finite slab edge to reveal.
const WORLD_COLOR = "#e9edf3";

const el = (id: string): HTMLElement => document.getElementById(id)!;

// The floor grid: square `TILE`-metre cells drawn as line segments, lying just
// above the ground plane. A small shader fades each fragment's opacity with its
// radial distance from the centre, so the lines dissolve towards the horizon
// (no hard rim around a finite slab) while staying crisp under the model. The
// colour and softness match the studio's pale, flat palette.
function buildFadingGrid(): THREE.LineSegments {
  const verts: number[] = [];
  for (let v = -GRID_EXTENT; v <= GRID_EXTENT + 1e-6; v += TILE) {
    verts.push(v, 0, -GRID_EXTENT, v, 0, GRID_EXTENT); // line parallel to Z
    verts.push(-GRID_EXTENT, 0, v, GRID_EXTENT, 0, v); // line parallel to X
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));

  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uColor: { value: new THREE.Color("#aab4c4") },
      uNear: { value: 0 },
      uFar: { value: 1 },
      uOpacity: { value: 0.55 },
    },
    vertexShader: `
      varying vec3 vWorld;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorld = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uNear;
      uniform float uFar;
      uniform float uOpacity;
      varying vec3 vWorld;
      void main() {
        float d = length(vWorld.xz);
        float a = (1.0 - smoothstep(uNear, uFar, d)) * uOpacity;
        if (a <= 0.001) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  });

  const grid = new THREE.LineSegments(geometry, material);
  grid.position.y = 0.002; // lift off the ground plane to avoid z-fighting
  return grid;
}

// The footprint patch: a tinted, outlined `w`×`h` block of tiles centred on the
// model, showing exactly which cells the model is meant to occupy on the game
// grid. Built to sit flush on the (parity-snapped) grid lines, so an odd side
// reads as a single centre tile and an even side as a shared edge between two.
function buildFootprintPatch(w: number, h: number): THREE.Group {
  const g = new THREE.Group();
  const ww = w * TILE, hh = h * TILE;
  const fill = new THREE.Mesh(
    new THREE.PlaneGeometry(ww, hh),
    new THREE.MeshBasicMaterial({ color: "#5b8def", transparent: true, opacity: 0.14, depthWrite: false }),
  );
  fill.rotation.x = -Math.PI / 2;
  fill.position.y = 0.004;
  g.add(fill);
  const hx = ww / 2, hz = hh / 2;
  const border = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-hx, 0.006, -hz), new THREE.Vector3(hx, 0.006, -hz),
      new THREE.Vector3(hx, 0.006, hz), new THREE.Vector3(-hx, 0.006, hz),
    ]),
    new THREE.LineBasicMaterial({ color: "#3f6fd1", transparent: true, opacity: 0.85 }),
  );
  g.add(border);
  return g;
}

// Free a group's geometries and materials before dropping it from the scene.
function disposeGroup(group: THREE.Group): void {
  group.traverse((o) => {
    const m = o as THREE.Mesh | THREE.Line;
    if (m.geometry) m.geometry.dispose();
    const mat = (m as THREE.Mesh).material;
    if (mat) (Array.isArray(mat) ? mat : [mat]).forEach((x) => x.dispose());
  });
}

export class DetailViewer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -100, 200);
  private readonly pivot = new THREE.Object3D();
  // The model is unlit — its faceted shading is baked into the vertex colours by
  // buildModel (the geometry has no normals a lit material would need). It still
  // casts a shadow onto the lit pedestal/ground below.
  private readonly modelMaterial = new THREE.MeshBasicMaterial({ vertexColors: true });
  private readonly canvas: HTMLCanvasElement;
  private readonly stage: HTMLElement;
  // The two floor treatments the model can sit on: the default pedestal (rebuilt
  // per model to fit it) and the optional fading grid (built once). `showGrid`
  // is which one is live; `currentHeight`/`currentRadius` cache the model's size
  // so the floor can be re-staged on a toggle without a full rebuild.
  private pedestal?: THREE.Mesh;
  private readonly grid = buildFadingGrid();
  // The tinted patch of grid tiles under a model that declares a `tiles`
  // footprint — rebuilt per model to its w×h size, shown only in grid mode.
  private footprintPatch?: THREE.Group;
  private showGrid = false;
  private currentHeight = 0.1;
  private currentRadius = 0.2;
  private instance?: ModelInstance;
  private interInstance?: InteractionInstance;
  // What is mounted in each of the staged model's sockets: socket id → content
  // model id (a socket absent from the map is empty). When any are filled, the
  // stage shows the host + its mounted content baked into one static mesh
  // (`socketMesh`) rather than the live `instance`. Cleared whenever the staged
  // object changes (open / version switch); kept across a variant switch.
  private readonly fills = new Map<string, string>();
  private socketMesh?: THREE.Mesh;
  private socketGeo?: THREE.BufferGeometry;

  // Selection + render state.
  private group!: DetailGroup;
  private versionIndex = 0;
  private current!: DetailItem;
  private variantIndex = 0;
  private animId: string | null = null;
  private interId: string | null = null;
  // Chosen recolour-slot overrides for the object on the stage (e.g. a team
  // colour). Empty for a model that exposes no slots. Reset each time an object
  // opens, so a red worker does not bleed onto the next object.
  private slots: Record<string, ColorRepresentation> = {};
  // Pending debounce timer for the thumbnail rebuild while a colour is being
  // dragged in the free picker. The live stage recolours on every input; only
  // the expensive thumbnail re-render is coalesced.
  private slotThumbTimer: number | null = null;
  private playing = false;
  private animElapsed = 0;
  private spin = true;
  private spinAngle = 0;
  private userYaw = 0;
  private userPitch = 0;
  private zoom = 1.4;
  private readonly target = new THREE.Vector3(0, 0.5, 0);

  private stageW = 1;
  private stageH = 1;
  private running = false;
  // Active pointers by id, each tracking its own last position. One pointer
  // orbits the camera (swipe = yaw, drag up/down = pitch); two pointers pinch to
  // zoom. Tracking per pointer means dropping one of a pinch leaves the other's
  // baseline correct, so rotation does not jump when a finger lifts.
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private dragging = false;
  private pinchStartDist = 0;
  private pinchStartZoom = 1;
  // The glide a swipe leaves behind (radians/s), decayed each frame for inertia.
  private yawVel = 0;
  private pitchVel = 0;
  private lastMoveTime = 0;
  private prev = 0;

  constructor(private readonly opts: DetailOptions) {
    this.canvas = el("detail-view") as HTMLCanvasElement;
    this.stage = el("stage");
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene.background = new THREE.Color(WORLD_COLOR);
    // Keep the world's far edges tinted the exact background colour so distant
    // grid lines fade into it rather than ending on a hard line.
    this.scene.fog = new THREE.Fog(WORLD_COLOR, 44, 70);
    addStudioLights(this.scene, true);

    // The floor is a transparent shadow-catcher, not a coloured slab: it shows
    // only the model's soft contact shadow and otherwise lets the background show
    // straight through. So the ground is the same endless field as the background
    // (no finite square appears when you zoom out) while the model still casts a
    // grounding shadow.
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(120, 120),
      new THREE.ShadowMaterial({ opacity: 0.18 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);

    this.grid.visible = false; // off until the toolbar toggle turns it on
    this.scene.add(this.grid);

    this.pivot.position.y = PED_H;
    this.scene.add(this.pivot);

    this.wireControls();
  }

  /** Show `group` at version `versionIndex`: build the version switcher, rebuild
   *  the stage, reframe, refill the inspector and start the loop. */
  open(group: DetailGroup, versionIndex = 0, variantId?: string): void {
    this.group = group;
    this.versionIndex = Math.max(0, Math.min(versionIndex, group.members.length - 1));
    this.current = group.members[this.versionIndex]!;
    const vi = variantId ? this.current.model.variants.findIndex((v) => v.id === variantId) : -1;
    this.variantIndex = vi >= 0 ? vi : this.defaultVariantIndex(this.current.model);
    this.animId = null;
    this.interId = null;
    this.playing = false;
    this.animElapsed = 0;
    this.userYaw = 0;
    this.userPitch = 0;
    this.spinAngle = 0;
    this.yawVel = 0;
    this.pitchVel = 0;
    this.pointers.clear();
    this.dragging = false;
    // Each freshly opened object starts spinning again: dragging only retires the
    // auto-spin for the object you are looking at, it is not a sticky preference.
    this.spin = true;
    (el("spin") as HTMLInputElement).checked = true;
    this.fills.clear(); // a freshly opened object starts with empty sockets
    this.slots = {}; // each object opens in its authored default colours
    this.resize();
    this.buildVersions();
    this.buildVariantChips();
    this.buildSocketSlots();
    this.buildFitsInto();
    this.buildTeamControls();
    this.rebuild(true);
    this.reportState(); // normalise the URL to the variant actually shown
    if (!this.running) {
      this.running = true;
      this.prev = performance.now();
      requestAnimationFrame(this.frame);
    }
  }

  // ── Version switcher ───────────────────────────────────────────────────────
  // A row of preview cards, one per version, each its own small thumbnail drawn
  // through the shared studio. Two presentations, switched purely in CSS by
  // viewport: on desktop the row sits centered at the top of the stage, always
  // visible; on mobile it is tucked behind the top-right toggle and dropped down
  // on demand. The `open` class drives the mobile panel; desktop ignores it and
  // shows the row regardless. A single-version object still gets its lone card on
  // desktop (matching the original layout) but no toggle on mobile.
  private buildVersions(): void {
    const wrap = el("versions");
    const toggle = el("versions-toggle");
    wrap.innerHTML = "";
    wrap.classList.remove("open"); // mobile panel starts collapsed
    toggle.classList.remove("on");

    const multi = this.group.members.length > 1;
    toggle.hidden = !multi; // the mobile toggle only appears when there is a choice
    el("versions-toggle-count").textContent = multi ? String(this.group.members.length) : "";

    const row = document.createElement("div");
    row.className = "ver-row";
    this.group.members.forEach((mem, i) => {
      const card = document.createElement("button");
      card.className = "ver-card" + (i === this.versionIndex ? " on" : "");
      card.title = mem.model.name;
      card.innerHTML = `<canvas width="160" height="160"></canvas><span class="ver-name">${versionLabel(mem.model)}</span>`;
      const canvas = card.querySelector("canvas") as HTMLCanvasElement;
      this.opts.renderThumb(canvas, mem.model, mem.model.defaultVariantId, false, this.activeSlots());
      card.addEventListener("click", () => this.selectVersion(i));
      row.appendChild(card);
    });
    wrap.appendChild(row);
  }

  private selectVersion(i: number): void {
    if (i === this.versionIndex || !this.group.members[i]) return;
    this.versionIndex = i;
    this.current = this.group.members[i]!;
    this.variantIndex = this.defaultVariantIndex(this.current.model);
    this.animId = null;
    this.interId = null;
    this.playing = false;
    this.animElapsed = 0;
    this.fills.clear(); // a different version publishes its own sockets
    this.buildVariantChips(); // a different version may define a different set of variants
    this.buildSocketSlots();
    this.buildFitsInto();
    this.rebuild(false); // same object, keep the camera framing steady
    const cards = [...el("versions").querySelectorAll(".ver-card")];
    cards.forEach((c, j) => c.classList.toggle("on", j === i));
    this.toggleVersions(false); // collapse the panel once a version is chosen
    this.reportState();
  }

  /** Show or hide the mobile version switcher panel. With no argument it flips
   *  the current state; pass `false`/`true` to force it closed/open. Only has a
   *  visible effect on mobile — desktop CSS shows the row regardless. */
  private toggleVersions(force?: boolean): void {
    const wrap = el("versions");
    const open = force ?? !wrap.classList.contains("open");
    wrap.classList.toggle("open", open);
    el("versions-toggle").classList.toggle("on", open);
  }

  /** Park the render loop (the viewer is hidden). */
  close(): void {
    this.running = false;
  }

  // ── Scene build + framing ──────────────────────────────────────────────────
  private rebuild(reframe: boolean): void {
    this.instance?.dispose();
    this.interInstance?.dispose(); this.interInstance = undefined;
    this.clearSocketMesh();
    // Detach whatever was staged before building the next graph. `dispose` frees
    // GPU buffers but does not unparent the object from the pivot (and an
    // interaction keeps its meshes), so without this the previous model — most
    // visibly a posed interaction — lingers behind every object opened after it.
    for (const child of [...this.pivot.children]) this.pivot.remove(child);
    this.instance = instantiateModel(this.current.model, { material: this.modelMaterial, slots: this.activeSlots() });

    const variant = this.current.model.variants[this.variantIndex] ?? this.current.model.variants[0]!;
    this.instance.setVariant(variant.id);

    // An interaction (if one is picked and offered on this variant) shows the
    // model coupled to a posed actor; otherwise the plain model + its animation.
    const interaction = this.interId
      ? this.current.model.interactions.find((it) => it.id === this.interId && this.offeredOn(it, variant.id))
      : undefined;
    const actor = interaction ? this.opts.resolveModel(DEFAULT_ACTOR) : undefined;
    // Sockets the user has loaded — ignored while an interaction is posed (the two
    // don't combine), so the picker and a posed actor stay mutually exclusive.
    const fills = interaction && actor ? [] : this.activeFills();

    let activeObject: THREE.Object3D;
    let bb: THREE.Box3;
    if (interaction && actor) {
      this.interInstance = instantiateInteraction(this.current.model, actor, interaction, { material: this.modelMaterial, variantId: variant.id, slots: this.activeSlots() });
      this.pivot.add(this.interInstance.object);
      activeObject = this.interInstance.object;
      bb = this.interInstance.bounds;
    } else if (fills.length) {
      // Carrying something: bake the host + its mounted content into one static
      // mesh. Host animations are dropped while loaded (the models that publish
      // sockets carry no animations of their own), so this stays a still pose.
      // `bakeSockets` preserves each part's recolour `slot`, so any team-colour
      // override still lands on the carried figure via applySlots.
      this.interId = null;
      this.socketGeo = buildModel(applySlots(bakeSockets(this.current.model, fills, { variantId: variant.id }), this.activeSlots()));
      this.socketMesh = new THREE.Mesh(this.socketGeo, this.modelMaterial);
      this.socketMesh.castShadow = this.socketMesh.receiveShadow = true;
      this.pivot.add(this.socketMesh);
      activeObject = this.socketMesh;
      bb = (this.socketGeo.boundingBox ?? new THREE.Box3().setFromObject(this.socketMesh)).clone();
    } else {
      this.interId = null; // not available on this variant / no actor in the catalogue
      this.pivot.add(this.instance.object);
      this.instance.setAnimation(this.animId);
      activeObject = this.instance.object;
      bb = this.instance.bounds;
    }

    const cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;
    activeObject.position.set(-cx, -bb.min.y, -cz);
    const radius = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z) / 2 || 0.2;
    const height = Math.max(bb.max.y - bb.min.y, 0.1);

    this.currentHeight = height;
    this.currentRadius = radius;
    if (reframe) {
      if (this.pedestal) { this.scene.remove(this.pedestal); (this.pedestal.geometry as THREE.BufferGeometry).dispose(); }
      const pr = Math.max(radius * 1.3, 0.18);
      this.pedestal = new THREE.Mesh(
        new THREE.CylinderGeometry(pr, pr * 1.08, PED_H, 32),
        new THREE.MeshLambertMaterial({ color: "#cdd5e1" }),
      );
      this.pedestal.position.y = PED_H / 2;
      this.pedestal.receiveShadow = true;
      this.pedestal.visible = !this.showGrid;
      this.scene.add(this.pedestal);
      // Frame the larger of the model and its declared tile footprint, so a big
      // footprint under a small model is not clipped when the grid is shown.
      const tiles = this.current.model.tiles;
      const tileReach = tiles ? (Math.max(tiles.w, tiles.h) * TILE) / 2 : 0;
      this.zoom = Math.max(radius, height, tileReach) * 1.5 + 0.25;
      this.applyFloor();
    }
    this.updateInfo(height, radius);
    this.markActiveVariant();
    this.updateControls();
  }

  // Stage the model on whichever floor is live: in pedestal mode it sits on the
  // pedestal (lifted by PED_H); in grid mode it sits on the grid at the ground.
  // Switches the visible floor and re-aims the camera target at the model's
  // centre without rebuilding geometry, so a toggle keeps the user's framing.
  private applyFloor(): void {
    const baseY = this.showGrid ? 0 : PED_H;
    this.pivot.position.y = baseY;
    this.grid.visible = this.showGrid;
    if (this.pedestal) this.pedestal.visible = !this.showGrid;

    // Seat the floor grid under the model by parity: an even side falls on a
    // grid line (offset 0), an odd side straddles a tile's centre (offset half a
    // tile), so a declared `tiles` footprint always lands on whole cells — one
    // centre tile for an odd side, a shared edge across two for an even one.
    const tiles = this.current.model.tiles;
    this.grid.position.x = tiles && tiles.w % 2 === 1 ? TILE / 2 : 0;
    this.grid.position.z = tiles && tiles.h % 2 === 1 ? TILE / 2 : 0;

    // (Re)build the tinted footprint patch for this model and show it with the grid.
    if (this.footprintPatch) {
      this.scene.remove(this.footprintPatch);
      disposeGroup(this.footprintPatch);
      this.footprintPatch = undefined;
    }
    if (tiles) {
      this.footprintPatch = buildFootprintPatch(tiles.w, tiles.h);
      this.footprintPatch.visible = this.showGrid;
      this.scene.add(this.footprintPatch);
    }

    // Fade the grid out in a ring around the model: solid out to the larger of
    // the model's radius and its tile footprint, gone one metre beyond.
    const tileReach = tiles ? (Math.max(tiles.w, tiles.h) * TILE) / 2 : 0;
    const solid = Math.max(this.currentRadius, tileReach);
    const u = (this.grid.material as THREE.ShaderMaterial).uniforms;
    u.uNear.value = solid;
    u.uFar.value = solid + GRID_FADE_MARGIN;
    this.target.set(0, baseY + this.currentHeight * 0.45, 0);
    this.applyCamera();
  }

  // Orbit the camera around the target. Drag and auto-spin move the *camera*
  // through the scene (so the ground, pedestal and grid swing past too) rather than
  // spinning the model on a fixed stage.
  private applyCamera(): void {
    const aspect = this.stageW / this.stageH;
    const c = this.camera;
    c.top = this.zoom; c.bottom = -this.zoom;
    c.left = -this.zoom * aspect; c.right = this.zoom * aspect;
    const azim = AZIM + this.spinAngle + this.userYaw;
    const pitch = THREE.MathUtils.clamp(PITCH + this.userPitch, MIN_PITCH, MAX_PITCH);
    const cp = Math.cos(pitch), sp = Math.sin(pitch);
    c.position.set(this.target.x + 40 * cp * Math.sin(azim), this.target.y + 40 * sp, this.target.z + 40 * cp * Math.cos(azim));
    c.up.set(0, 1, 0);
    c.lookAt(this.target);
    c.updateProjectionMatrix();
  }

  private resize(): void {
    this.stageW = this.stage.clientWidth || 1;
    this.stageH = this.stage.clientHeight || 1;
    this.renderer.setSize(this.stageW, this.stageH, false);
    this.applyCamera();
  }

  // ── Inspector ──────────────────────────────────────────────────────────────
  private updateInfo(height: number, radius: number): void {
    const m = this.current.model;
    // The title is the object; the version switcher and the id below pin down
    // which version is on the stage.
    el("info-name").textContent = this.group.name;
    // The id line also carries the batch this version was added in: id · batch.
    // The batch is computed once here and reused in the meta list below.
    const batch = batchOf(m);
    // Sync provenance: a glowing "Out of sync" flag when the model has been
    // edited here but not yet synced back to age-of-mcp (synced === false); a
    // quiet "Synced" when explicitly in sync; nothing for the untracked baseline.
    const syncFlag = m.synced === false
      ? ` <span class="sync-flag sync-out" title="Edited here — not yet synced to age-of-mcp">Out of sync</span>`
      : m.synced === true
        ? ` <span class="sync-flag sync-ok" title="In sync with age-of-mcp">Synced</span>`
        : "";
    el("info-id").innerHTML = m.id + (batch
      ? ` <span class="dot" aria-hidden="true">·</span> ` +
        `<button type="button" class="batch-link" title="${batch.style.replace(/"/g, "&quot;")}">${batch.label}</button>`
      : "") + syncFlag;
    el("info-summary").textContent = m.summary ?? "";
    const desc = el("info-desc"); desc.textContent = m.description ?? ""; desc.style.display = m.description ? "" : "none";

    // A tag with editorial metadata shows its prettier label and explains itself
    // on hover; every other tag shows its raw string. Clicking always filters by
    // the underlying tag.
    const tags = el("info-tags"); tags.innerHTML = "";
    for (const t of m.tags) {
      const chip = document.createElement("span");
      chip.className = "tag"; chip.textContent = tagLabel(t);
      const meta = tagMeta(t);
      if (meta) { chip.classList.add("described"); chip.title = meta.description; }
      chip.addEventListener("click", () => this.opts.onTag(t));
      tags.appendChild(chip);
    }

    // The batch is this version's provenance: which run generated it (with the
    // generating model + style brief on hover). The label clicks through to the
    // batch's own page (all the models from that run).
    const batchRow = batch
      ? `<dt>batch</dt><dd><button type="button" class="batch-link" ` +
        `title="${batch.style.replace(/"/g, "&quot;")}">${batch.label}</button> ` +
        `<span class="muted">(${batch.generator})</span></dd>` +
        `<dt>for</dt><dd>${batch.for}</dd>`
      : "";
    // The bare counts (versions / variants / animations / interactions) are left
    // out: they only restate what the version switcher, variant grid and the
    // animation/interaction controls already show. Only descriptive metadata stays.
    el("info-meta").innerHTML =
      `<dt>author</dt><dd>${m.author ?? "—"}</dd>` +
      batchRow +
      `<dt>category</dt><dd>${this.current.category}</dd>` +
      (m.tiles ? `<dt>tiles</dt><dd>${m.tiles.w}×${m.tiles.h}</dd>` : "") +
      `<dt>size</dt><dd>${(radius * 2).toFixed(2)}w × ${height.toFixed(2)}h</dd>`;
    if (batch) {
      // Both the id-line batch and the meta-list batch click through to the run.
      for (const link of document.querySelectorAll("#info-id .batch-link, #info-meta .batch-link")) {
        link.addEventListener("click", () => this.opts.onBatch(batch.id));
      }
    }
  }

  private updateControls(): void {
    const m = this.current.model;
    const variant = m.variants[this.variantIndex] ?? m.variants[0]!;
    const anims = this.instance?.animationsForVariant(variant.id) ?? [];
    // An object with nothing to animate (in this variant) hides the whole
    // Animation control rather than showing a lone, inert "None" dropdown.
    el("anim-controls").hidden = anims.length === 0;
    const animSel = el("anim-sel") as HTMLSelectElement;
    animSel.innerHTML = `<option value="">None</option>` +
      anims.map((a) => `<option value="${a.id}">${a.label ?? a.id}</option>`).join("");
    animSel.value = this.animId ?? "";
    animSel.disabled = anims.length === 0;

    // Interactions are only offerable when the catalogue has an actor to pose.
    const inters = this.opts.resolveModel(DEFAULT_ACTOR)
      ? m.interactions.filter((it) => this.offeredOn(it, variant.id))
      : [];
    el("inter-controls").hidden = inters.length === 0;
    const interSel = el("inter-sel") as HTMLSelectElement;
    interSel.innerHTML = `<option value="">None</option>` +
      inters.map((it) => `<option value="${it.id}">${it.label ?? it.id}</option>`).join("");
    interSel.value = this.interId ?? "";
    interSel.disabled = inters.length === 0;

    this.updatePlayIcon();
    this.updateTimeline();
  }

  /** Is interaction `it` offered on `variantId` (no restriction, or it lists it)? */
  private offeredOn(it: Interaction, variantId: string): boolean {
    return !it.variants || it.variants.includes(variantId);
  }

  private stepVariant(d: number): void {
    const n = this.current.model.variants.length;
    if (n <= 1) return;
    this.selectVariant((this.variantIndex + d + n) % n);
  }

  // Switch to variant `i` (from a chip click or the arrow keys) and rebuild —
  // animation state resets, since a variant may not share the previous one.
  private selectVariant(i: number): void {
    const n = this.current.model.variants.length;
    if (i === this.variantIndex || i < 0 || i >= n) return;
    this.variantIndex = i;
    this.animId = null; this.interId = null; this.playing = false; this.animElapsed = 0;
    this.rebuild(false);
    this.reportState();
    const variant = this.current.model.variants[i];
    if (variant) this.opts.onVariantPick?.(variant.id);
  }

  /** Index of the model's default variant — the one shown when no deep-link
   *  variant is given. Decoupled from order, so a staged building defaults to
   *  `complete` even though it is listed last. */
  private defaultVariantIndex(model: Model): number {
    const i = model.variants.findIndex((v) => v.id === model.defaultVariantId);
    return i >= 0 ? i : 0;
  }

  /** Tell the host which model/variant is on the stage so it can pin the URL.
   *  The default variant reports `null` (a bare deep link), whichever its
   *  position; only an off-default variant pins its id. */
  private reportState(): void {
    const variant = this.current.model.variants[this.variantIndex];
    const isDefault = !variant || variant.id === this.current.model.defaultVariantId;
    this.opts.onState(this.current.model.id, isDefault ? null : variant!.id);
  }

  // The inspector's visual variant browser: one preview thumbnail per state /
  // stage, drawn through the shared studio (via renderThumb). Rebuilt whenever the
  // model changes (open or version switch); hidden for single-variant models,
  // where the big stage already is the only preview.
  private buildVariantChips(): void {
    const m = this.current.model;
    const wrap = el("variants-wrap");
    const grid = el("variants");
    grid.innerHTML = "";
    if (m.variants.length <= 1) { wrap.hidden = true; return; }
    wrap.hidden = false;
    el("variants-count").textContent = String(m.variants.length);
    m.variants.forEach((v, i) => {
      const chip = document.createElement("button");
      chip.className = "variant-chip";
      chip.title = v.label ?? v.id; // the name lives in the tooltip and the header line
      chip.innerHTML = `<span class="variant-thumb"><canvas width="240" height="240"></canvas></span>`;
      // The variant tiles are small, so fill them more tightly than the gallery
      // default to make out what differs between states (sized to the model, so
      // big builds still fit whole).
      this.opts.renderThumb(chip.querySelector("canvas") as HTMLCanvasElement, m, v.id, true, this.activeSlots());
      chip.addEventListener("click", () => this.selectVariant(i));
      grid.appendChild(chip);
    });
  }

  private markActiveVariant(): void {
    const chips = el("variants").children;
    for (let i = 0; i < chips.length; i++) chips[i].classList.toggle("active", i === this.variantIndex);
    const variant = this.current.model.variants[this.variantIndex];
    el("variant-current").textContent = variant ? (variant.label ?? variant.id) : "";
  }

  // ── Sockets (the content picker) ─────────────────────────────────────────────
  // The fills now loaded, as the `bakeSockets` input: each socket the user has
  // chosen content for, paired with that content model. Skips a fill whose model
  // the catalogue no longer holds, so a stale choice never throws the bake.
  private activeFills(): SocketFill[] {
    const out: SocketFill[] = [];
    for (const s of this.current.model.sockets) {
      const id = this.fills.get(s.id);
      if (!id) continue;
      const model = this.opts.resolveModel(id);
      if (model) out.push({ socket: s.id, model });
    }
    return out;
  }

  private clearSocketMesh(): void {
    if (this.socketMesh) { this.pivot.remove(this.socketMesh); this.socketMesh = undefined; }
    if (this.socketGeo) { this.socketGeo.dispose(); this.socketGeo = undefined; }
  }

  // Build the compact socket grid: one small tile per socket showing either a
  // "+" button (empty) or the filled model's thumbnail with a clear "x". Sockets
  // sharing a `slotType` are grouped under one heading. Clicking "+" opens a
  // modal picker overlay with the full candidate list (thumbnails + filter).
  private buildSocketSlots(): void {
    const m = this.current.model;
    const wrap = el("sockets-wrap");
    const host = el("sockets");
    host.innerHTML = "";
    if (m.sockets.length === 0) { wrap.hidden = true; return; }
    wrap.hidden = false;
    el("sockets-count").textContent = String(m.sockets.length);

    const groups = new Map<string, typeof m.sockets>();
    for (const s of m.sockets) {
      const key = s.slotType ?? s.id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(s);
    }

    for (const [key, sockets] of groups) {
      const section = document.createElement("div");
      section.className = "socket-group";
      const label = sockets.length > 1
        ? `${key} (${sockets.length})`
        : (sockets[0].description ?? key);
      // When the socket is governed by a known slot type, its heading clicks
      // through to that slot type's page — the contract plus every model that
      // fits it. The "+" tiles below still open the live picker to mount one.
      const slotTypeId = sockets[0].slotType;
      const stLabel = slotTypeId ? this.opts.slotTypeLabel(slotTypeId) : undefined;
      if (slotTypeId && stLabel) {
        const title = `See the ${stLabel} slot type — its contract and every model that fits it`.replace(/"/g, "&quot;");
        section.innerHTML =
          `<div class="socket-head"><button type="button" class="socket-link" title="${title}">` +
            `<span class="socket-name">${label}</span>` +
            `<svg class="socket-link-arrow" width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2l4 4-4 4"/></svg>` +
          `</button></div>`;
        section.querySelector(".socket-link")!.addEventListener("click", () => this.opts.onSlotType(slotTypeId));
      } else {
        section.innerHTML = `<div class="socket-head"><span class="socket-name">${label}</span></div>`;
      }
      const grid = document.createElement("div");
      grid.className = "socket-compact-grid";
      for (const socket of sockets) grid.appendChild(this.socketTile(socket));
      section.appendChild(grid);
      host.appendChild(section);
    }
  }

  // The reverse link: when this model is slotable (declares `fits`), list the
  // slot types it can mount into, each clicking through to that slot type's page
  // — its contract and, crucially, every host that accepts it.
  private buildFitsInto(): void {
    const m = this.current.model;
    const wrap = el("fits-wrap");
    const host = el("fits");
    host.innerHTML = "";
    const fits = m.fits ?? [];
    if (fits.length === 0) { wrap.hidden = true; return; }
    wrap.hidden = false;
    el("fits-count").textContent = String(fits.length);
    for (const stId of fits) {
      const label = this.opts.slotTypeLabel(stId) ?? stId;
      const known = this.opts.slotTypeLabel(stId) !== undefined;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "fits-link";
      btn.disabled = !known;
      btn.title = known
        ? `See the ${label} slot type — its contract and every host that accepts this`
        : label;
      btn.innerHTML =
        `<svg class="fits-brick" width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="3.6" y="3.3" width="2.4" height="2.9" rx="0.5"/><rect x="8" y="3.3" width="2.4" height="2.9" rx="0.5"/><rect x="2.4" y="5.9" width="9.2" height="5.6" rx="0.9"/></svg>` +
        `<span class="fits-name">${label}</span>` +
        (known ? `<svg class="socket-link-arrow" width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2l4 4-4 4"/></svg>` : "");
      if (known) btn.addEventListener("click", () => this.opts.onSlotType(stId));
      host.appendChild(btn);
    }
  }

  private socketTile(socket: { id: string; slotType?: string; accepts?: string[]; description?: string }): HTMLElement {
    const tile = document.createElement("button");
    tile.className = "socket-tile";
    tile.dataset.socket = socket.id;
    tile.title = socket.description ?? socket.id;
    const fillId = this.fills.get(socket.id);
    const filled = fillId ? this.opts.resolveModel(fillId) : undefined;
    if (filled) {
      tile.classList.add("socket-tile-filled");
      tile.innerHTML = `<canvas width="120" height="120"></canvas><span class="socket-tile-clear" title="Clear">x</span>`;
      this.opts.renderThumb(tile.querySelector("canvas") as HTMLCanvasElement, filled, filled.defaultVariantId, true);
      tile.querySelector(".socket-tile-clear")!.addEventListener("click", (e) => {
        e.stopPropagation();
        this.setFill(socket.id, null);
      });
      tile.addEventListener("click", () => this.openSocketPicker(socket));
    } else {
      tile.innerHTML = `<span class="socket-tile-plus">+</span>`;
      tile.addEventListener("click", () => this.openSocketPicker(socket));
    }
    return tile;
  }

  private openSocketPicker(socket: { id: string; slotType?: string; accepts?: string[]; description?: string }): void {
    let overlay = document.getElementById("socket-picker-overlay");
    if (overlay) overlay.remove();

    overlay = document.createElement("div");
    overlay.id = "socket-picker-overlay";
    overlay.innerHTML = `
      <div class="socket-picker-panel">
        <div class="socket-picker-header">
          <span class="socket-picker-title">${socket.description ?? socket.id}</span>
          <button class="socket-picker-close" title="Close">x</button>
        </div>
        <div class="socket-picker-grid"></div>
      </div>`;
    document.body.appendChild(overlay);

    const grid = overlay.querySelector(".socket-picker-grid") as HTMLElement;
    const closeBtn = overlay.querySelector(".socket-picker-close") as HTMLElement;

    const candidates = this.opts.mountablesFor(socket, this.current.model.id);

    grid.innerHTML = "";
    const empty = this.pickerOption(null, "Empty");
    empty.addEventListener("click", () => {
      this.setFill(socket.id, null);
      overlay!.remove();
    });
    grid.appendChild(empty);
    for (const c of candidates) {
      const opt = this.pickerOption(c, c.name);
      const fillId = this.fills.get(socket.id);
      if (fillId === c.id) opt.classList.add("active");
      opt.addEventListener("click", () => {
        this.setFill(socket.id, c.id);
        overlay!.remove();
      });
      grid.appendChild(opt);
    }

    closeBtn.addEventListener("click", () => overlay!.remove());
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) overlay!.remove();
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { overlay!.remove(); document.removeEventListener("keydown", onKey); }
    };
    document.addEventListener("keydown", onKey);
  }

  private pickerOption(model: Model | null, label: string): HTMLElement {
    const chip = document.createElement("button");
    chip.className = "socket-opt" + (model ? "" : " socket-empty");
    chip.title = label;
    if (model) {
      chip.innerHTML = `<span class="socket-thumb"><canvas width="200" height="200"></canvas></span><span class="socket-opt-name">${label}</span>`;
      this.opts.renderThumb(chip.querySelector("canvas") as HTMLCanvasElement, model, model.defaultVariantId, true);
    } else {
      chip.innerHTML = `<span class="socket-thumb socket-thumb-empty">&empty;</span><span class="socket-opt-name">${label}</span>`;
    }
    return chip;
  }

  /** Mount `modelId` in `socketId` (or clear it when null) and restage. */
  private setFill(socketId: string, modelId: string | null): void {
    if (modelId) this.fills.set(socketId, modelId);
    else this.fills.delete(socketId);
    this.interId = null;
    this.rebuild(false);
    this.buildSocketSlots();
  }

  // ── Team colour ──────────────────────────────────────────────────────────────
  // The slot overrides to instantiate with: the chosen map, or undefined when no
  // slot has been recoloured (so the model keeps its authored defaults).
  private activeSlots(): Record<string, ColorRepresentation> | undefined {
    return Object.keys(this.slots).length ? this.slots : undefined;
  }

  // Build the colour-picker panel for the current model's recolour slots (a row
  // of preset swatches plus a free colour input per slot). Hidden for any model
  // that exposes no slots, which is almost all of them.
  private buildTeamControls(): void {
    const wrap = el("team-wrap");
    const host = el("team-swatches");
    host.innerHTML = "";
    const slots = slotsOf(this.current.model);
    wrap.hidden = slots.length === 0;
    if (!slots.length) return;

    for (const slot of slots) {
      const row = document.createElement("div");
      row.className = "team-row";
      row.dataset.slot = slot.id;
      const def = String(slot.default);

      // Preset swatches: the authentic team palette plus the slot's own default.
      const presets = TEAM_PRESETS.includes(def) ? TEAM_PRESETS : [def, ...TEAM_PRESETS];
      for (const color of presets) {
        const sw = document.createElement("button");
        sw.type = "button";
        sw.className = "team-swatch";
        sw.style.background = color;
        sw.dataset.color = color.toLowerCase();
        sw.title = color;
        sw.addEventListener("click", () => this.pickSlot(slot.id, color));
        row.appendChild(sw);
      }

      // A free colour input, so any colour is reachable beyond the presets.
      const free = document.createElement("input");
      free.type = "color";
      free.className = "team-free";
      free.value = def;
      free.title = "Custom colour";
      // Dragging the free picker fires `input` continuously; recolour the live
      // stage on each move but coalesce the heavy thumbnail rebuild. `change`
      // (fired when the picker commits/closes) flushes a final, immediate
      // rebuild so the thumbnails always settle on the chosen colour.
      free.addEventListener("input", () => this.pickSlot(slot.id, free.value, true));
      free.addEventListener("change", () => this.pickSlot(slot.id, free.value));
      row.appendChild(free);

      host.appendChild(row);
    }
    this.markActiveSwatch();
  }

  // Apply a chosen colour to a slot and re-render the stage and the thumbnails.
  // The live stage recolour is always immediate; when `defer` is set (a drag on
  // the free picker) the expensive thumbnail rebuild is debounced so the picker
  // stays responsive instead of stuttering on every pointer move.
  private pickSlot(slotId: string, color: string, defer = false): void {
    this.slots[slotId] = color;
    // The live stage instance can recolour in place (no reframe); an interaction
    // instance is rebuilt by rebuild(). Both are cheap enough to do every frame.
    if (this.instance && !this.interInstance) this.instance.setSlots(this.activeSlots());
    else this.rebuild(false);
    this.markActiveSwatch();

    if (this.slotThumbTimer !== null) clearTimeout(this.slotThumbTimer);
    if (defer) {
      this.slotThumbTimer = window.setTimeout(() => this.refreshSlotThumbs(), 120);
    } else {
      this.slotThumbTimer = null;
      this.refreshSlotThumbs();
    }
  }

  // Redraw the version + variant-chip thumbnails with the current slot colours.
  // This is the costly part of a recolour (offscreen WebGL renders), so it is
  // debounced away from the live stage update during a drag.
  private refreshSlotThumbs(): void {
    this.slotThumbTimer = null;
    this.buildVersions();
    this.buildVariantChips();
  }

  // Outline the swatch matching each slot's current colour.
  private markActiveSwatch(): void {
    for (const row of [...el("team-swatches").querySelectorAll<HTMLElement>(".team-row")]) {
      const slotId = row.dataset.slot!;
      const current = this.slots[slotId] !== undefined ? String(this.slots[slotId]).toLowerCase() : undefined;
      for (const sw of [...row.querySelectorAll<HTMLElement>(".team-swatch")]) {
        sw.classList.toggle("on", current !== undefined && sw.dataset.color === current);
      }
    }
  }

  // Turn the auto-spin off and reflect it in the toolbar checkbox. Idempotent, so
  // it is safe to call on every pointermove of a drag.
  private disableSpin(): void {
    if (!this.spin) return;
    this.spin = false;
    (el("spin") as HTMLInputElement).checked = false;
  }

  private updatePlayIcon(): void {
    const a = el("anim-play") as HTMLButtonElement;
    a.textContent = this.playing && this.animId ? "⏸" : "▶";
    a.disabled = !this.animId;
    const i = el("inter-play") as HTMLButtonElement;
    i.textContent = this.playing && this.interId ? "⏸" : "▶";
    i.disabled = !this.interId;
  }

  // ── Timeline (scrubber) ──────────────────────────────────────────────────────
  /** The clip now selected for playback (an interaction takes precedence over an
   *  animation), or null when nothing is playable — what the scrubber scrubs. */
  private currentClip(): { duration: number; loop: boolean } | null {
    const m = this.current.model;
    if (this.interId) {
      const it = m.interactions.find((a) => a.id === this.interId);
      return it ? { duration: it.duration, loop: it.loop ?? true } : null;
    }
    if (this.animId) {
      const a = m.animations.find((x) => x.id === this.animId);
      return a ? { duration: a.duration, loop: a.loop ?? true } : null;
    }
    return null;
  }

  /** Pose whichever instance is active at time `t` (seconds). */
  private applyTime(t: number): void {
    if (this.interId && this.interInstance) this.interInstance.update(t);
    else if (this.animId && this.instance) this.instance.update(t);
  }

  /** Position within the current cycle (looped clips wrap; one-shots clamp). */
  private clipPos(clip: { duration: number; loop: boolean }): number {
    return clip.loop ? ((this.animElapsed % clip.duration) + clip.duration) % clip.duration
      : Math.min(this.animElapsed, clip.duration);
  }

  /** Show/refresh the scrubber for the current clip (hidden when none plays). */
  private updateTimeline(): void {
    const clip = this.currentClip();
    el("timeline").hidden = !clip;
    if (!clip) return;
    const scrub = el("time-scrub") as HTMLInputElement;
    scrub.max = clip.duration.toFixed(3);
    scrub.step = Math.max(0.01, clip.duration / 600).toFixed(3);
    const pos = this.clipPos(clip);
    scrub.value = String(pos);
    this.setTimeLabel(pos, clip.duration);
  }

  private setTimeLabel(t: number, duration: number): void {
    el("time-label").textContent = `${t.toFixed(1)}s / ${duration.toFixed(1)}s`;
  }

  /** Play/pause the current clip; restart a finished one-shot from the top. */
  private togglePlay(): void {
    const clip = this.currentClip();
    if (!clip) return;
    if (!this.playing && !clip.loop && this.animElapsed >= clip.duration) this.animElapsed = 0;
    this.playing = !this.playing;
    this.updatePlayIcon();
  }

  // ── Wiring ─────────────────────────────────────────────────────────────────
  private wireControls(): void {
    el("back").addEventListener("click", () => this.opts.onClose());
    el("versions-toggle").addEventListener("click", () => this.toggleVersions());

    const animSel = el("anim-sel") as HTMLSelectElement;
    animSel.addEventListener("change", () => {
      this.animId = animSel.value || null;
      if (this.animId) this.interId = null; // animation and interaction are mutually exclusive
      this.animElapsed = 0;
      this.rebuild(false); // swap any interaction graph out and set the animation
      this.playing = !!this.animId;
      this.updatePlayIcon();
    });
    el("anim-play").addEventListener("click", () => { if (this.animId) this.togglePlay(); });

    const interSel = el("inter-sel") as HTMLSelectElement;
    interSel.addEventListener("change", () => {
      this.interId = interSel.value || null;
      if (this.interId) this.animId = null;
      this.animElapsed = 0;
      this.rebuild(true); // build/teardown the actor graph and reframe to include it
      this.playing = !!this.interId;
      this.updatePlayIcon();
    });
    el("inter-play").addEventListener("click", () => { if (this.interId) this.togglePlay(); });

    // The timeline scrubber: drag to scrub the clip by hand (it pauses playback
    // and poses the model/actor at the chosen instant).
    const scrub = el("time-scrub") as HTMLInputElement;
    scrub.addEventListener("input", () => {
      const clip = this.currentClip();
      if (!clip) return;
      this.playing = false;
      this.animElapsed = Number(scrub.value);
      this.applyTime(this.animElapsed);
      this.setTimeLabel(this.animElapsed, clip.duration);
      this.updatePlayIcon();
    });

    (el("spin") as HTMLInputElement).addEventListener("change", (e) => {
      this.spin = (e.target as HTMLInputElement).checked;
    });

    (el("grid-toggle") as HTMLInputElement).addEventListener("change", (e) => {
      this.showGrid = (e.target as HTMLInputElement).checked;
      this.applyFloor(); // swap the floor in place, keeping the current framing
    });

    this.canvas.addEventListener("pointerdown", (e) => {
      this.canvas.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.dragging = true;
      // A fresh grab kills any leftover inertial glide so the model stops dead
      // under the finger rather than fighting the touch.
      this.yawVel = 0; this.pitchVel = 0;
      this.lastMoveTime = e.timeStamp;
      if (this.pointers.size === 2) this.beginPinch();
    });
    this.canvas.addEventListener("pointermove", (e) => {
      const p = this.pointers.get(e.pointerId);
      if (!p) return;
      const dx = e.clientX - p.x, dy = e.clientY - p.y;
      p.x = e.clientX; p.y = e.clientY;
      // The moment the user takes the camera into their own hands, retire the
      // auto-spin for good — it must not creep back the instant they let go.
      this.disableSpin();
      if (this.pointers.size >= 2) { this.updatePinch(); return; }
      this.orbit(dx, dy, e.timeStamp);
    });
    const endPointer = (e: PointerEvent): void => {
      if (!this.pointers.delete(e.pointerId)) return;
      this.dragging = this.pointers.size > 0;
      // Dropping from two fingers to one: re-arm the pinch baseline against the
      // pair that remains, and stop the lone glide from inheriting pinch motion.
      if (this.pointers.size === 2) this.beginPinch();
    };
    this.canvas.addEventListener("pointerup", endPointer);
    this.canvas.addEventListener("pointercancel", endPointer);
    this.canvas.addEventListener("wheel", (e) => {
      e.preventDefault();
      this.zoom = THREE.MathUtils.clamp(this.zoom * (e.deltaY > 0 ? 1.1 : 0.9), ZOOM_MIN, ZOOM_MAX);
      this.applyCamera();
    }, { passive: false });

    window.addEventListener("resize", () => { if (this.running) this.resize(); });
    window.addEventListener("keydown", (e) => {
      if (!this.running) return;
      const t = e.target as HTMLElement;
      if (t && (t.tagName === "INPUT" || t.tagName === "SELECT")) return;
      if (e.key === "Escape") { e.preventDefault(); this.opts.onClose(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); this.stepVariant(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); this.stepVariant(-1); }
    });
  }

  // ── Orbit + pinch (mouse and touch) ─────────────────────────────────────────
  // Apply one drag step to the camera and remember its speed, so a finger lifted
  // mid-swipe leaves a glide (the inertia the frame loop coasts down).
  private orbit(dx: number, dy: number, time: number): void {
    const dYaw = -dx * ROT_SPEED;
    const dPitch = dy * ROT_SPEED;
    this.userYaw += dYaw;
    this.userPitch = THREE.MathUtils.clamp(this.userPitch + dPitch, MIN_PITCH - PITCH, MAX_PITCH - PITCH);
    const dt = (time - this.lastMoveTime) / 1000;
    if (dt > 0 && dt < 0.1) { this.yawVel = dYaw / dt; this.pitchVel = dPitch / dt; }
    this.lastMoveTime = time;
  }

  /** Distance between the two active touch points, in CSS pixels (0 if not a pair). */
  private pinchDistance(): number {
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return 0;
    return Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
  }

  /** Anchor a pinch: remember the current finger spread and zoom to scale from. */
  private beginPinch(): void {
    this.pinchStartDist = this.pinchDistance();
    this.pinchStartZoom = this.zoom;
    this.yawVel = 0; this.pitchVel = 0; // a pinch is not a swipe; no glide afterwards
  }

  /** Scale the zoom by how far the fingers have spread since the pinch began
   *  (apart = zoom in, together = zoom out), mirroring the wheel's range. */
  private updatePinch(): void {
    const d = this.pinchDistance();
    if (d <= 0 || this.pinchStartDist <= 0) return;
    this.zoom = THREE.MathUtils.clamp(this.pinchStartZoom * this.pinchStartDist / d, ZOOM_MIN, ZOOM_MAX);
    this.applyCamera();
  }

  // ── Frame loop ─────────────────────────────────────────────────────────────
  private frame = (now: number): void => {
    if (!this.running) return;
    requestAnimationFrame(this.frame);
    const dt = Math.min((now - this.prev) / 1000, 0.05); this.prev = now;
    if (this.spin && !this.dragging) this.spinAngle -= dt * SPIN_RATE;
    // Coast on after a swipe: keep orbiting along the released velocity, bleeding
    // it off each frame until it falls below a hair's breadth and snaps to rest.
    if (!this.dragging && (this.yawVel !== 0 || this.pitchVel !== 0)) {
      this.userYaw += this.yawVel * dt;
      const pitch = THREE.MathUtils.clamp(this.userPitch + this.pitchVel * dt, MIN_PITCH - PITCH, MAX_PITCH - PITCH);
      if (pitch === this.userPitch) this.pitchVel = 0; // hit the pitch stop — kill that axis
      this.userPitch = pitch;
      const decay = Math.pow(GLIDE_DECAY, dt);
      this.yawVel *= decay; this.pitchVel *= decay;
      if (Math.abs(this.yawVel) < GLIDE_STOP) this.yawVel = 0;
      if (Math.abs(this.pitchVel) < GLIDE_STOP) this.pitchVel = 0;
    }
    this.applyCamera();
    if (this.playing) {
      const clip = this.currentClip();
      if (clip) {
        this.animElapsed += dt;
        if (!clip.loop && this.animElapsed >= clip.duration) {
          this.animElapsed = clip.duration; this.playing = false; this.updatePlayIcon();
        }
        this.applyTime(this.animElapsed);
        const pos = this.clipPos(clip);
        (el("time-scrub") as HTMLInputElement).value = String(pos);
        this.setTimeLabel(pos, clip.duration);
      }
    }
    this.renderer.render(this.scene, this.camera);
  };
}
